import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import getMailItemSortByProperty from '../../../utils/tables/getMailItemSortByProperty';
import onLoadInitialRowsSucceeded from '../onLoadInitialRowsSucceeded';
import { action } from 'satcheljs/lib/legacy';
import { findItemWithStartIndex } from 'owa-mail-store';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { INITIAL_LOAD_ROW_COUNT } from '../../data-loading/TableLoadingConstants';
import { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { ServiceFetchError } from 'owa-service/lib/ServiceFetchError';
import {
    getBaseFolderId,
    MailFolderTableQuery,
    TableView,
    getBaseServerFolderId,
} from 'owa-mail-list-store';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';
import getHashtagsToInclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToInclude';

/**
 * Load the initial items from the server
 * @param tableView to load items in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
export default action('loadInitialItemsFromServer')(function loadInitialItemsFromServer(
    tableView: TableView,
    isTablePrefetched: boolean,
    onInitialTableLoadComplete: OnInitialTableLoadComplete
): Promise<void> {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;

    return findItemWithStartIndex(
        getBaseFolderId(mailTableQuery),
        0 /* startIndex */,
        INITIAL_LOAD_ROW_COUNT,
        mailTableQuery.viewFilter,
        getMailItemSortByProperty(mailTableQuery),
        mailTableQuery.focusedViewFilter,
        mailTableQuery.requestShapeName,
        mailTableQuery.shouldTryToUseSessionDataResponse,
        getMailboxInfoFromTableQuery(mailTableQuery).requestOptions,
        mailTableQuery.categoryName,
        getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
        getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
        getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */)
    ).then(
        (responseMessage: FindItemResponseMessage) => {
            let returnedPromise = Promise.resolve();
            const isSuccessResponseClass = responseMessage.ResponseClass == 'Success';
            if (isSuccessResponseClass) {
                onLoadInitialRowsSucceeded(
                    tableView,
                    responseMessage.RootFolder.Items,
                    responseMessage.RootFolder.TotalItemsInView,
                    responseMessage.SearchFolderId,
                    null /* FolderId used for groups scenario, we currently don't support message view in groups. */
                );
            } else {
                const error = new Error(
                    `ResponseCode=${responseMessage.ResponseCode}, Stacktrace=${
                        responseMessage.StackTrace
                    }`
                ) as ServiceFetchError;

                error.fetchErrorType = 'ServerFailure';

                returnedPromise = Promise.reject(error);
            }

            onInitialTableLoadComplete(
                tableView,
                isSuccessResponseClass,
                responseMessage.ResponseCode,
                isTablePrefetched
            );

            return returnedPromise;
        },
        error => {
            // Server returned non-200 response
            const responseCode = (error && error.response && error.response.status) || '';
            onInitialTableLoadComplete(
                tableView,
                false /* isSuccessResponseClass */,
                responseCode,
                isTablePrefetched
            );

            return Promise.reject(error);
        }
    );
});
